import { DesktopDatePicker } from "@mui/x-date-pickers";
import { formatDateDayjs, globalDateFormat } from "@qubit/autoparts";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import { Inv_InventoryDateLabelSettings } from "~/config/clientConfig";
import {
  getMinInventoryExpirationDate,
  inventoryDateLabel
} from "~/lib/helpers";

import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";

import { selectIsPreviousBinFlow } from "./autostorePutaway.slice";

type Props = {
  inventoryDate: Dayjs | null;
  inv_inventoryDateLabel: Inv_InventoryDateLabelSettings;
  isDateValid: boolean;
  isPastDateAllowed?: boolean;
  setInventoryDate: (date: Dayjs | null) => void;
  onChangeCallback?: () => void;
};

export function InventoryDateField(props: Props) {
  const {
    inventoryDate,
    inv_inventoryDateLabel,
    isDateValid,
    isPastDateAllowed,
    setInventoryDate,
    onChangeCallback
  } = props;
  const { t } = useTranslation();
  const trackedPageName = useMixpanelPageName();
  const isPreviousBinFlow = useAppSelector(selectIsPreviousBinFlow);
  const dateLabel = inventoryDateLabel(inv_inventoryDateLabel);
  const capitalizedDateLabelForTracking =
    dateLabel.charAt(0).toUpperCase() + dateLabel.slice(1);

  return (
    <DesktopDatePicker
      slotProps={{
        textField: {
          InputLabelProps: {
            shrink: true
          },
          onBlur: (e) => {
            mixpanelTrack({
              trackedPageName,
              type: "Input Change",
              label: `${capitalizedDateLabelForTracking} (Manual Input)`,
              eventProperties: {
                Date: e.target.value
              }
            });
          },
          label: t(inventoryDateLabel(inv_inventoryDateLabel)),
          variant: "outlined",
          fullWidth: true,
          error: !isDateValid,
          sx: {
            ".MuiInputBase-input": {
              fontSize: "22px"
            }
          }
        }
      }}
      label={t(inventoryDateLabel(inv_inventoryDateLabel))}
      value={
        inventoryDate?.isValid() ? dayjs(formatDateDayjs(inventoryDate)) : null
      }
      format={globalDateFormat}
      onChange={(date) => {
        if (onChangeCallback) onChangeCallback();
        setInventoryDate(date);
      }}
      onAccept={(date) => {
        mixpanelTrack({
          trackedPageName,
          type: "Select Option",
          label: `${capitalizedDateLabelForTracking} (Calendar Select)`,
          eventProperties: {
            Date: date ? date.format(globalDateFormat) : null
          }
        });
      }}
      onOpen={() => {
        mixpanelTrack({
          trackedPageName,
          type: "Button Click",
          label: `Open ${capitalizedDateLabelForTracking} Calendar`
        });
      }}
      disabled={isPreviousBinFlow}
      reduceAnimations
      minDate={getMinInventoryExpirationDate(
        inv_inventoryDateLabel,
        isPastDateAllowed
      )}
    />
  );
}

export default InventoryDateField;
